import { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';
import { createEditor, NodeEntry, Path, Range, Transforms } from 'slate';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';
import styled from 'styled-components';

import { useMetrics } from '../../hooks/useMetrics';
import { WordTiming } from '../../utils/ttsAlignment';
import {
  Element,
  ElementProps,
  Leaf,
  parseStringToSlateObject,
  SlateWrapper,
  TEXT_ALIGN_TYPES,
} from '../SlideComponents/slateUtil';

interface Props {
  content?: string | null;
  id: string;
  style?: CSSProperties;
  forceState?: { align?: TEXT_ALIGN_TYPES; color?: string; size: number };
  wordTimings: WordTiming[];
  currentWordIndex: number;
}

const ReadAloudHighlightedTextViewer = ({
  content,
  id,
  style,
  forceState,
  wordTimings,
  currentWordIndex,
}: Props) => {
  const metrics = useMetrics();
  const editor = useMemo(() => withReact(createEditor()), []);
  const highlightRef = useRef<HTMLSpanElement | null>(null);

  const renderElement = useCallback(
    (props: ElementProps) => {
      const isMathNode =
        props.element.type === 'math-inline' || props.element.type === 'math-block';

      let mathHighlightStyle: CSSProperties = {};

      if (isMathNode && currentWordIndex >= 0 && currentWordIndex < wordTimings.length) {
        const word = wordTimings[currentWordIndex];
        try {
          const path = ReactEditor.findPath(editor, props.element);
          // Math (inline or block) is highlighted as a whole element: the word's
          // path points at the math element itself, so match the element's path.
          const isHighlighted = Path.equals(path, word.path);

          if (isHighlighted) {
            mathHighlightStyle = {
              background: 'rgba(249, 151, 70, 0.35)',
              borderRadius: '3px',
              transition: 'background 0.1s ease',
            };
          }
        } catch {
          // element not (yet) in editor — skip
        }
      }

      return (
        <Element
          {...props}
          attributes={{
            ...props.attributes,
            style: {
              ...props.attributes?.style,
              marginBottom: '12px',
              ...mathHighlightStyle,
            },
          }}
        />
      );
    },
    [currentWordIndex, wordTimings, editor],
  );

  const renderLeaf = useCallback(
    (props: any) => {
      const { attributes, children, leaf } = props;
      if (leaf.ttsHighlight) {
        return (
          <HighlightSpan
            {...attributes}
            ref={(el: HTMLSpanElement | null) => {
              // Store ref for auto-scroll
              highlightRef.current = el;
              // Forward Slate's ref
              if (typeof attributes.ref === 'function') {
                attributes.ref(el);
              } else if (attributes.ref) {
                attributes.ref.current = el;
              }
            }}
          >
            {children}
          </HighlightSpan>
        );
      }
      return <Leaf {...props} metrics={metrics} />;
    },
    [metrics],
  );

  const decorate = useCallback(
    ([node, path]: NodeEntry) => {
      const ranges: Range[] = [];

      if (currentWordIndex < 0 || currentWordIndex >= wordTimings.length) {
        return ranges;
      }

      const word = wordTimings[currentWordIndex];

      // Only decorate the text leaf at the word's exact path. The `'text' in node`
      // guard means math elements (whose path points at the element, not a leaf)
      // are never decorated here — they're highlighted in renderElement instead.
      if ('text' in node && Path.equals(path, word.path)) {
        ranges.push({
          anchor: { path, offset: word.slateCharStart },
          focus: { path, offset: word.slateCharEnd },
          // @ts-expect-error - custom decoration property
          ttsHighlight: true,
        });
      }

      return ranges;
    },
    [currentWordIndex, wordTimings],
  );

  const loadedContent = useMemo(() => {
    return parseStringToSlateObject(content);
  }, [content]);

  useEffect(() => {
    Transforms.deselect(editor);
    const contentToLoad = loadedContent.map(c => ({ ...c, ...forceState }));
    editor.children = contentToLoad;
    editor.onChange();
  }, [forceState, loadedContent, editor]);

  // Auto-scroll to highlighted word
  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [currentWordIndex]);

  return (
    <SlateWrapper style={style ?? {}}>
      <span translate="no">
        <Slate editor={editor} value={loadedContent}>
          <Editable
            id={id}
            readOnly={true}
            style={style}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            decorate={decorate}
          />
        </Slate>
      </span>
    </SlateWrapper>
  );
};

const HighlightSpan = styled.span`
  background: rgba(249, 151, 70, 0.35);
  border-radius: 3px;
  padding: 1px 0;
  transition: background 0.1s ease;
`;

export default ReadAloudHighlightedTextViewer;
